<template>
  <div
    class="rating-breakdown"
    :class="{ desktop: $isDesktop, 'show-overall-rating': showOverallRating }"
  >
    <table>
      <thead>
        <template v-if="showOverallRating">
          <tr>
            <th colspan="3" class="overall-rating">
              <div>
                <translate tag="h4"> Overall </translate>
                <span class="cs-overall-rating">
                  <pu-rating-bubble
                    :rating="getRoundedRating('rateOverall')"
                    :force-visibility="true"
                    class="user-rating"
                  />
                  <translate class="out-of" :tt="{ count: 10 }">
                    out of {count}
                  </translate>
                </span>
              </div>
            </th>
          </tr>
        </template>
      </thead>
      <tbody>
        <tr v-for="category in categories" :key="category.id" class="category">
          <th>
            <div><span class="icon"></span>{{ category.name }}</div>
          </th>
          <td class="graph">
            <span class="bar" v-if="getRatingPercentage(category.id)">
              <span
                class="bar-value"
                :style="{ width: getRatingPercentage(category.id) + '%' }"
              /> </span
            ><span v-else class="no-rating">{{ defaultValue }}</span>
          </td>
          <td align="middle">
            <span v-if="getRatingPercentage(category.id)" class="pc">{{
              getRoundedRating(category.id)
            }}</span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts">
import {
  Component,
  Prop,
  Vue,
  Setup,
  toNative,
} from '~/utility/pu-class-decorator'
import { RatingBreakdown } from '../types'

@Component
class PuRatingBreakdown extends Vue {
  @Setup(() => {
    const { t } = useI18n()
    return t('No rating')
  })
  defaultValue: string

  @Prop()
  breakdown!: RatingBreakdown

  @Prop({ default: true })
  showOverallRating: boolean

  getRoundedRating(categoryId) {
    return Math.round(this.breakdown[categoryId])
  }

  getRatingPercentage(categoryId) {
    return Math.round(this.breakdown[categoryId]) * 10
  }

  get categories() {
    const { t } = useI18n()
    return [
      { id: 'rateLocation', name: t('Location') },
      { id: 'rateValue', name: t('Value for money') },
      { id: 'rateQuality', name: t('Accommodation') },
      { id: 'rateFacilities', name: t('Facilities / Activities') },
      { id: 'rateService', name: t('Customer service') },
      { id: 'rateChildFriendly', name: t('Child friendliness') },
      { id: 'rateClean', name: t('Cleanliness') },
      { id: 'rateWifi', name: t('Wifi') },
    ]
  }
}

export default toNative(PuRatingBreakdown)
</script>

<style scoped>
.rating-breakdown {
  margin: 1.5em 1.4em;
}

table {
  width: clamp(200px, 100%, 24em);
  font-size: var(--body);
}

tr {
  border: none;
}

th,
td {
  vertical-align: middle;
  padding: 0;
}

th {
  min-width: 18ch;
  padding: 0.6em 0.6em 0.6em 0;
}

td.graph {
  width: 100%;
}

.bar {
  background-color: #eaedf0;
  width: 100%;
  height: 10px;
  position: relative;
  display: block;
  border-radius: 2em;
  min-width: 4em;
}

.bar-value {
  background-color: var(--colour-light-green);
  width: 0;
  height: 10px;
  display: block;
  position: absolute;
  left: 0;
  top: 0;
  border-radius: 2em;
}

.pc {
  padding: 0 0 0 0.4em;
}

.no-rating {
  color: #aaa;
  font-size: smaller;
}

th div {
  display: flex;
  align-items: center;
  justify-content: start;
  line-height: 1;
}

h4 {
  color: var(--colour-dark-blue);
  font-size: 1.3em;
  margin-bottom: 0.85em;
}

th.overall-rating {
  padding-top: 0;
}

.overall-rating div {
  padding-bottom: 0.75em;
  border-bottom: 1px solid var(--colour-page-background-light);
}

.cs-overall-rating {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: 0.6em;
}

.cs-overall-rating .user-rating {
  font-size: 1.25em;
}

.out-of {
  color: var(--colour-text-light);
  font-size: var(--small);
  margin-top: 0.5em;
  white-space: nowrap;
}

.icon {
  width: 1em;
  height: 1em;
  display: inline-block;
  margin-right: 0.5em;
  background-repeat: no-repeat;
  background-position: center;
  flex-shrink: 0;
}

.category:nth-of-type(1) .icon {
  background-image: url('@/assets/review-icons/location.svg');
}

.category:nth-of-type(2) .icon {
  background-image: url('@/assets/images/creditcard.svg');
}

.category:nth-of-type(3) .icon {
  background-image: url('@/assets/review-icons/accommodation.svg');
}

.category:nth-of-type(4) .icon {
  background-image: url('@/assets/review-icons/facilities.svg');
}

.category:nth-of-type(5) .icon {
  background-image: url('@/assets/review-icons/customer-service.svg');
}

.category:nth-of-type(6) .icon {
  background-image: url('@/assets/review-icons/child-friendly.svg');
}

.category:nth-of-type(7) .icon {
  background-image: url('@/assets/review-icons/cleanliness.svg');
}

.category:nth-of-type(8) .icon {
  background-image: url('@/assets/review-icons/wifi.svg');
}

/* desktop & tablet */
.desktop.rating-breakdown {
  margin: 0;
}

table.desktop th,
table.desktop td {
  padding-top: 0.1em;
  padding-bottom: 0.1em;
}

.desktop.rating-breakdown th,
.desktop.rating-breakdown td {
  padding: 0.15em 0.4em 0.15em 0;
}

.desktop.rating-breakdown .bar,
.desktop.rating-breakdown .bar-value {
  height: 7px;
}

.desktop.rating-breakdown .overall-rating div {
  padding-bottom: 0.5em;
  border: none;
  justify-content: end;
}

.desktop.rating-breakdown h4 {
  margin-bottom: 1em;
  font-weight: normal;
}
</style>
